import { useTheme } from '@emotion/react'
import LinkOutlined from '@mui/icons-material/LinkOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
    Avatar,
    AvatarGroup,
    Box,
    Container,
    Grid,
    IconButton,
    Paper,
    Stack,
    Typography,
} from '@mui/material'
import { format } from 'date-fns'
import { useMemo, useState } from 'react'
import RosterStatusPill from 'recruiter/components/RosterStatusPill'
import { recruiterRouteConfig } from 'recruiter/routes'
import useIsMobile from 'shared/hooks/useIsMobile'
import { getFullName, getInitials, getPxAsRem } from 'shared/utils'
import {
    ActionButtonsContainer,
    LabelTypography,
    MobileFullScreenContainer,
    MobileInfoBox,
    StyledLink,
    ValueTypography,
} from '../styled'

import type {
    RosterProfileQuery,
    RosterProfileQueryVariables,
} from '__generated__/graphql'
import type { FC } from 'react'

import IndustryOrSectorList from 'recruiter/components/IndustryOrSectorList'
import type { ApolloQueryRefetchFunction } from 'shared/types/graphql'
import RosterAccessIndicator from '../../../../../shared/components/RosterAccessIndicator'
import ActionsMenu from './ActionsMenu'
import RosterHeaderDonutGraph from './DonutGraph'
import { CandidatePipeline } from './CandidatePipeline'

interface RosterSearchHeaderProps {
    data: RosterProfileQuery['roster']
    refetch: ApolloQueryRefetchFunction<
        RosterProfileQuery,
        RosterProfileQueryVariables
    >
}

/**
 * Gets the full URL to a roster's page on the client portal
 * by replacing the recruiter subdomain with client in the current URL
 */
const getClientPortalRosterUrl = (rosterId: string): string => {
    const { protocol, hostname, port } = window.location

    // Replace only the first instance of recruiter (in subdomain)
    // Handles: dev-recruiter.cpiny.com -> dev-client.cpiny.com
    // Handles: recruiter.localhost -> client.localhost
    const clientHostname = hostname.replace('recruiter', 'client')

    // Include port if it exists (for localhost)
    const portSuffix = port ? `:${port}` : ''
    const baseUrl = `${protocol}//${clientHostname}${portSuffix}`

    return `${baseUrl}/searches/${rosterId}`
}

const RosterSearchHeader: FC<RosterSearchHeaderProps> = ({ data, refetch }) => {
    const isMobile = useIsMobile()
    const theme = useTheme()
    const industries = data?.industries ?? []
    const sectors = data?.sectors ?? []
    const memoizedSectors = useMemo(() => data?.sectors, [data?.sectors])
    const memoizedIndustries = useMemo(
        () => data?.industries,
        [data?.industries]
    )
    const placements = data?.placements ?? []
    const company = data?.company?.value ?? '-'
    const companyOffices =
        data?.companyOffices?.map((co) => co.companyOffice) ?? []
    const status = data?.status ?? ''
    const formattedDate = data?.createdAt
        ? format(new Date(data.createdAt), 'MMM dd, yyyy')
        : null
    const clientContacts = data?.clientContacts ?? []
    const totalPlacementCount = data?.placements?.reduce(
        (sum, item) => sum + (item.placementCount || 0),
        0
    )
    const sharedClients = data?.sharedClients ?? []
    const sharedRecruiters = data?.sharedRecruiters ?? []

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const openMenu = Boolean(anchorEl)

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const validIndustries = useMemo(
        () =>
            (memoizedIndustries || [])
                .map(({ industry }) => industry)
                .filter((industry) => !!industry)
                .map((industry) => ({ industry })),
        [memoizedIndustries]
    )

    const validSectors = useMemo(
        () =>
            (memoizedSectors || [])
                .map(({ sector }) => sector)
                .filter((sector) => !!sector)
                .map((sector) => ({ sector })),
        [memoizedSectors]
    )

    const handleClose = () => {
        setAnchorEl(null)
    }

    const SharedWith = ({ children }) => (
        <AvatarGroup
            max={3}
            sx={{
                '& .MuiAvatarGroup-avatar': {
                    width: getPxAsRem(24),
                    height: getPxAsRem(24),
                    padding: '0.2rem',
                    fontSize: '0.7rem',
                },
                width: 'max-content',
            }}
        >
            {children}
        </AvatarGroup>
    )

    if (isMobile) {
        return (
            <MobileFullScreenContainer>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <RosterHeaderDonutGraph data={data} />
                    <Typography variant="h4" gutterBottom>
                        {data?.rosterName ?? '-'}
                    </Typography>
                    <Typography variant="caption">
                        {`Created ${formattedDate} by ${data?.createdByUser && getFullName(data?.createdByUser, false)}`}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        <RosterStatusPill
                            status={status}
                            sx={{ width: 'fit-content' }}
                        />
                    </Box>
                </Box>

                <Grid container spacing={4} sx={{ paddingX: getPxAsRem(28) }}>
                    <Grid item xs={4} sm={4}>
                        <MobileInfoBox>
                            <ValueTypography variant="overline">
                                TITLES
                            </ValueTypography>
                            <Typography variant="body2">
                                {placements.length}
                            </Typography>
                        </MobileInfoBox>
                    </Grid>
                    <Grid item xs={4} sm={4}>
                        <MobileInfoBox>
                            <ValueTypography variant="overline">
                                INDUSTRIES
                            </ValueTypography>
                            <Typography variant="body2">
                                {industries.length}
                            </Typography>
                        </MobileInfoBox>
                    </Grid>
                    <Grid item xs={4} sm={4}>
                        <MobileInfoBox>
                            <ValueTypography variant="overline">
                                SECTORS
                            </ValueTypography>
                            <Typography variant="body2">
                                {sectors.length}
                            </Typography>
                        </MobileInfoBox>
                    </Grid>
                </Grid>
                <Grid
                    container
                    spacing={4}
                    sx={{
                        paddingX: getPxAsRem(28),
                        marginTop: getPxAsRem(20),
                    }}
                >
                    <Grid item xs={4} sm={4}>
                        <MobileInfoBox>
                            <ValueTypography variant="overline">
                                LOCATIONS
                            </ValueTypography>
                            <Typography variant="body2">
                                {companyOffices.length}
                            </Typography>
                        </MobileInfoBox>
                    </Grid>
                    <Grid item xs={4} sm={4}>
                        <MobileInfoBox>
                            <ValueTypography variant="overline">
                                START DATE
                            </ValueTypography>
                            <Typography variant="body2">
                                {data?.startDates
                                    ?.map((sd) => sd.startDateLabel)
                                    .join(', ') || '-'}
                            </Typography>
                        </MobileInfoBox>
                    </Grid>
                    <Grid item xs={4} sm={4}>
                        <MobileInfoBox>
                            <ValueTypography variant="overline">
                                OPENINGS
                            </ValueTypography>
                            <Typography variant="body2">
                                {totalPlacementCount}
                            </Typography>
                        </MobileInfoBox>
                    </Grid>
                </Grid>

                <Grid
                    container
                    spacing={4}
                    sx={{
                        paddingX: getPxAsRem(28),
                        marginTop: getPxAsRem(20),
                    }}
                >
                    <Grid item xs={4} sm={4}>
                        <MobileInfoBox>
                            <ValueTypography variant="overline">
                                CPI CONTACT
                            </ValueTypography>
                            <Typography variant="body2">
                                {clientContacts.length}
                            </Typography>
                        </MobileInfoBox>
                    </Grid>
                    <Grid item xs={4} sm={4}>
                        <MobileInfoBox>
                            <ValueTypography variant="overline">
                                COMPANY
                            </ValueTypography>
                            <StyledLink to={`/companies/${data?.company?.id}`}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {company}
                                </Typography>
                            </StyledLink>
                        </MobileInfoBox>
                    </Grid>

                    <Grid item xs={4} sm={4}>
                        <MobileInfoBox>
                            <ValueTypography variant="overline">
                                CLIENTS ACCESS
                            </ValueTypography>
                            <Typography variant="body2">
                                {sharedClients.length}
                            </Typography>
                        </MobileInfoBox>
                    </Grid>
                </Grid>

                <Grid
                    container
                    spacing={4}
                    sx={{
                        paddingX: getPxAsRem(28),
                        marginTop: getPxAsRem(20),
                    }}
                >
                    <Grid item xs={4} sm={4}>
                        <MobileInfoBox>
                            <ValueTypography variant="overline">
                                INTERNAL USERS
                            </ValueTypography>
                            <Typography variant="body2">
                                {sharedRecruiters.length}
                            </Typography>
                        </MobileInfoBox>
                    </Grid>
                    <Grid item xs={4} sm={4}>
                        <MobileInfoBox>
                            <ValueTypography variant="overline">
                                CLIENT VIEW
                            </ValueTypography>
                            <Box
                                component="a"
                                href={getClientPortalRosterUrl(data?.id || '')}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    textDecoration: 'none',
                                    color: theme.palette.primary.main,
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                <Typography variant="body2">
                                    Portal link
                                </Typography>
                                <LinkOutlined
                                    sx={{
                                        ml: 0.5,
                                        fontSize: '0.9rem',
                                    }}
                                />
                            </Box>
                        </MobileInfoBox>
                    </Grid>
                </Grid>

                {/* Candidate Pipeline */}
                <Box sx={{ mt: 6, mb: 3, px: getPxAsRem(28) }}>
                    <CandidatePipeline roster={data} />
                </Box>
            </MobileFullScreenContainer>
        )
    }

    return (
        <Stack
            component={Paper}
            borderRadius="8px"
            direction="row"
            justifyContent="space-between"
            position="relative"
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                }}
            >
                <RosterHeaderDonutGraph data={data} />
                <Stack
                    direction="column"
                    alignItems="flex-start"
                    sx={{
                        flexGrow: 1,
                        padding: getPxAsRem(20, 0),
                        marginLeft: -5,
                    }}
                >
                    <Container
                        maxWidth={false}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                        }}
                    >
                        <Box>
                            <Stack
                                direction="row"
                                spacing={1.5}
                                sx={{ display: 'flex', alignItems: 'center' }}
                            >
                                <Typography variant="h3">
                                    {data?.rosterName}
                                </Typography>

                                <RosterStatusPill
                                    status={status}
                                    sx={{ width: 'fit-content' }}
                                />
                                <RosterAccessIndicator
                                    isLocked={!data?.permissions?.canEdit}
                                />
                            </Stack>
                            <Typography
                                color={theme.palette.greyscale.grey_600}
                                variant="caption"
                            >
                                {`Created ${formattedDate} by ${data?.createdByUser && getFullName(data?.createdByUser, false)}`}
                            </Typography>
                        </Box>
                    </Container>

                    <Box sx={{ marginTop: getPxAsRem(5) }}>
                        <Grid
                            container
                            spacing={6}
                            sx={{ paddingX: getPxAsRem(36) }}
                        >
                            <Grid item xs={12} sm={3} md={3} lg={2}>
                                <LabelTypography variant="overline">
                                    TITLES
                                </LabelTypography>
                                <ValueTypography
                                    variant="body2"
                                    title={placements
                                        .map((item) => item.title?.value)
                                        .join(', ')}
                                >
                                    {placements?.length
                                        ? placements.map((item, index) => (
                                              <span key={index}>
                                                  {item.title?.value}
                                                  {index <
                                                      placements.length - 1 &&
                                                      ','}{' '}
                                              </span>
                                          ))
                                        : '-'}
                                </ValueTypography>
                            </Grid>

                            <Grid item xs={12} sm={3} md={3} lg={2}>
                                <LabelTypography variant="overline">
                                    INDUSTRY
                                </LabelTypography>

                                <IndustryOrSectorList
                                    items={validIndustries}
                                    limit={1}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3} md={3} lg={3}>
                                <LabelTypography variant="overline">
                                    SECTOR
                                </LabelTypography>

                                <IndustryOrSectorList
                                    items={validSectors}
                                    limit={1}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3} md={3} lg={2}>
                                <LabelTypography variant="overline">
                                    LOCATION
                                </LabelTypography>
                                <ValueTypography
                                    variant="body2"
                                    title={companyOffices
                                        .map(
                                            (item) =>
                                                item?.officeLocation?.value
                                        )
                                        .join(', ')}
                                >
                                    {companyOffices.length
                                        ? companyOffices.map((item, index) => (
                                              <span key={index}>
                                                  {item?.officeLocation?.value}
                                                  {index <
                                                      companyOffices.length -
                                                          1 && ','}{' '}
                                              </span>
                                          ))
                                        : '-'}
                                </ValueTypography>
                            </Grid>
                            <Grid item xs={12} sm={3} md={3} lg={2}>
                                <LabelTypography variant="overline">
                                    START DATE
                                </LabelTypography>
                                <ValueTypography variant="body2">
                                    {data?.startDates
                                        ?.map((sd) => sd.startDateLabel)
                                        .join(', ') || '-'}
                                </ValueTypography>
                            </Grid>
                            <Grid item xs={12} sm={3} md={3} lg={1}>
                                <LabelTypography variant="overline">
                                    OPENINGS
                                </LabelTypography>
                                <ValueTypography variant="body2">
                                    {totalPlacementCount}
                                </ValueTypography>
                            </Grid>
                        </Grid>

                        <Grid
                            container
                            spacing={3}
                            sx={{
                                paddingX: getPxAsRem(36),
                                marginTop: '0.12rem',
                            }}
                        >
                            <Grid item xs={12} sm={3} md={3} lg={2}>
                                <LabelTypography
                                    variant="overline"
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    CLIENT CONTACT
                                </LabelTypography>
                                <ValueTypography
                                    variant="body2"
                                    title={clientContacts
                                        .map(
                                            (item) =>
                                                item.candidate?.user &&
                                                getFullName(
                                                    item.candidate?.user,
                                                    false
                                                )
                                        )
                                        .join(', ')}
                                >
                                    {clientContacts.length > 0
                                        ? clientContacts?.map((item, index) => (
                                              <span key={index}>
                                                  {item.candidate?.user &&
                                                      getFullName(
                                                          item.candidate?.user,
                                                          false
                                                      )}
                                                  {index <
                                                      clientContacts.length -
                                                          1 && ','}{' '}
                                              </span>
                                          ))
                                        : '-'}
                                </ValueTypography>
                            </Grid>
                            <Grid item xs={12} sm={3} md={3} lg={2}>
                                <LabelTypography variant="overline">
                                    COMPANY
                                </LabelTypography>
                                <StyledLink
                                    to={`${recruiterRouteConfig.companies.path}/${data?.company?.id}`}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <ValueTypography
                                        variant="body2"
                                        title={company}
                                    >
                                        {company}
                                    </ValueTypography>
                                </StyledLink>
                            </Grid>
                            <Grid item xs={12} sm={3} md={3} lg={2}>
                                <LabelTypography
                                    variant="overline"
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    CLIENT ACCESS
                                </LabelTypography>
                                <Box
                                    title={sharedClients
                                        .map(
                                            (item) =>
                                                item.client?.user &&
                                                getFullName(
                                                    item.client?.user,
                                                    false
                                                )
                                        )
                                        .join(', ')}
                                >
                                    {sharedClients?.length ? (
                                        <SharedWith>
                                            {sharedClients.map(
                                                (client, index) => (
                                                    <Avatar
                                                        key={index}
                                                        src={
                                                            client.client?.user
                                                                ?.profileImageKey ||
                                                            ''
                                                        }
                                                    >
                                                        {client.client?.user &&
                                                            getInitials(
                                                                client.client
                                                                    ?.user
                                                            )}
                                                    </Avatar>
                                                )
                                            )}
                                        </SharedWith>
                                    ) : (
                                        '-'
                                    )}
                                </Box>
                            </Grid>

                            <Grid item xs={12} sm={3} md={3} lg={2}>
                                <LabelTypography
                                    variant="overline"
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    CLIENT VIEW
                                </LabelTypography>
                                <Box
                                    component="a"
                                    href={getClientPortalRosterUrl(
                                        data?.id || ''
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        textDecoration: 'none',
                                        color: theme.palette.primary.main,
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                >
                                    <ValueTypography variant="body2">
                                        Portal link
                                    </ValueTypography>
                                    <LinkOutlined
                                        sx={{
                                            ml: 0.5,
                                            fontSize: '1rem',
                                        }}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} sm={3} md={3} lg={2}>
                                <LabelTypography
                                    variant="overline"
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    INTERNAL USERS
                                </LabelTypography>
                                <Box
                                    title={sharedRecruiters
                                        .map(
                                            (item) =>
                                                item.recruiter?.user &&
                                                getFullName(
                                                    item.recruiter?.user,
                                                    false
                                                )
                                        )
                                        .join(', ')}
                                >
                                    {sharedRecruiters?.length ? (
                                        <SharedWith>
                                            {sharedRecruiters.map(
                                                (recruiter, index) => (
                                                    <Avatar
                                                        key={index}
                                                        src={
                                                            recruiter.recruiter
                                                                ?.user
                                                                ?.profileImageKey ||
                                                            ''
                                                        }
                                                    >
                                                        {recruiter.recruiter
                                                            ?.user &&
                                                            getInitials(
                                                                recruiter
                                                                    .recruiter
                                                                    ?.user
                                                            )}
                                                    </Avatar>
                                                )
                                            )}
                                        </SharedWith>
                                    ) : (
                                        '-'
                                    )}
                                </Box>
                            </Grid>
                        </Grid>

                        {data?.scratchPad && (
                            <Grid
                                container
                                spacing={3}
                                sx={{
                                    paddingX: getPxAsRem(36),
                                    marginTop: '0.12rem',
                                }}
                            >
                                <Grid item xs={12}>
                                    <LabelTypography variant="overline">
                                        SCRATCH PAD
                                    </LabelTypography>
                                    <Typography
                                        variant="body2"
                                        sx={{ whiteSpace: 'pre-wrap' }}
                                    >
                                        {data.scratchPad}
                                    </Typography>
                                </Grid>
                            </Grid>
                        )}

                        {/* Candidate Pipeline */}
                        <Box sx={{ mt: 3, px: getPxAsRem(36) }}>
                            <CandidatePipeline roster={data} />
                        </Box>
                    </Box>
                </Stack>
            </Box>
            {/* Action buttons */}
            {data?.permissions?.canEdit && (
                <>
                    <ActionButtonsContainer>
                        <IconButton onClick={handleClick}>
                            <MoreVertIcon color="primary" />
                        </IconButton>
                    </ActionButtonsContainer>
                    <ActionsMenu
                        open={openMenu}
                        anchorEl={anchorEl}
                        data={data}
                        handleClose={handleClose}
                        refetch={refetch}
                    />
                </>
            )}
        </Stack>
    )
}

export default RosterSearchHeader
